import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
    Box,
    Typography,
    IconButton,
    MenuItem,
    MenuList,
    ListItemText,
    Tooltip,
    Popover,
} from '@mui/material';
// components
import { Iconify } from '../../../../components';
import { CustomAvatar } from '../../../../components/custom-avatar';
import { normalizeActionLink } from '../../../../layouts/dashboard/header/NotificationsPopover';
// redux
import { useSelector } from '../../../../redux/store';
// utils
import { setLeadSourceIcon } from '../../../../utils/timelineIcon';

// ----------------------------------------------------------------------

export default function NotificationListRow({ row, markAsRead, isLast }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const { referral } = useSelector((state) => state.schema);

    const navigate = useNavigate();

    const handleOpenMenu = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleAvatar = (data) => {
        if (data?.verb === 'reminder_overdue') {
            return '/assets/icons/notification/reminder-overdue.png';
        }
        if (data?.verb === 'reminder_due_today') {
            return '/assets/icons/notification/task_due.png';
        }
        if (data?.verb === 'reminder_assigned_to_user') {
            return '/assets/icons/notification/assign_task.png';
        }
        if (data?.verb === 'phone_number_clicked') {
            return '/assets/icons/notification/call-in.png';
        }
        if (data?.verb === 'lead_assigned_to_user') {
            return '/assets/icons/notification/assign_lead.png';
        }
        if (data?.verb === 'new_lead') {
            return '/assets/icons/notification/user.png';
        }
        if (data?.verb === 'facebook_post_comment_created') {
            return '/assets/icons/notification/facebook.png';
        }
        if (data?.verb === 'gmb_review_created') {
            return '/assets/icons/notification/task_due.png';
        }
        return {};
    };

    const handleUrl = (data) => {
        if (data.verb === 'gmb_review_created') {
            navigate('/dashboard/manage-business');
            if (data.unread) {
                markAsRead(data.id, data.unread);
            }
            return;
        }
        if (data.action_link !== '') {
            navigate(`/dashboard/${normalizeActionLink(data.action_link)}`);
            if (data.unread) {
                markAsRead(data.id, data.unread);
            }
        }
    };

    const getVerbLabel = (verb) => {
        if (!verb) return '';
        return verb.charAt(0).toUpperCase() + verb.slice(1).replace(/_/g, ' ');
    };

    const toolTipText = (data) => (
        <div style={{ lineHeight: '15px', textAlign: 'center' }}>
            {getVerbLabel(data.verb)} <br />
            {data.verb === 'new_lead' && (`Source: ${setLeadSourceIcon(data?.action_object?.lead_source?.referral, referral)?.label || 'Unknown'}`)}
        </div>
    );

    const hasLeadSource = row?.action_object?.lead_source?.referral;
    const sourceIcon = hasLeadSource ? setLeadSourceIcon(hasLeadSource, referral) : null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 1.5,
                cursor: row.action_link ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease',
                borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                '&:hover': {
                    bgcolor: '#f8fafc',
                },
            }}
            onClick={() => handleUrl(row)}
        >
            {/* Unread indicator dot */}
            <Box
                sx={{
                    width: 8,
                    minWidth: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: row.unread ? '#3b82f6' : 'transparent',
                    flexShrink: 0,
                }}
            />

            {/* Avatar */}
            <Tooltip title={toolTipText(row)} arrow>
                <Box sx={{ flexShrink: 0 }}>
                    {!hasLeadSource ? (
                        <CustomAvatar
                            src={handleAvatar(row)}
                            sx={{
                                width: 44,
                                height: 44,
                                bgcolor: '#f1f5f9',
                                borderRadius: '12px',
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#eff6ff',
                                borderRadius: '12px',
                            }}
                        >
                            <Iconify
                                icon={sourceIcon?.icon}
                                sx={{ color: '#3b82f6' }}
                                width={24}
                                height={24}
                            />
                        </Box>
                    )}
                </Box>
            </Tooltip>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: row.unread ? 600 : 400,
                        color: row.unread ? '#1e293b' : '#64748b',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {row.description}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: row.unread ? '#3b82f6' : '#94a3b8',
                        fontWeight: row.unread ? 500 : 400,
                        mt: 0.25,
                    }}
                >
                    {row.timestamp_human}
                </Typography>
            </Box>

            {/* More menu */}
            <IconButton
                size="small"
                onClick={handleOpenMenu}
                sx={{
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    color: '#94a3b8',
                    '&:hover': {
                        bgcolor: '#f1f5f9',
                        color: '#64748b',
                    },
                }}
            >
                <Iconify icon="eva:more-horizontal-fill" width={18} />
            </IconButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                            minWidth: 180,
                            p: 0.5,
                        },
                    },
                }}
            >
                <MenuList sx={{ padding: 0 }}>
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(row.id, row.unread);
                            handleCloseMenu();
                        }}
                        sx={{
                            borderRadius: '8px',
                            minHeight: '40px !important',
                            gap: 1.5,
                        }}
                    >
                        <Iconify
                            icon={row.unread ? 'eva:done-all-fill' : 'eva:email-outline'}
                            sx={{ color: row.unread ? '#3b82f6' : '#64748b' }}
                            width={20}
                        />
                        <ListItemText
                            primary={row.unread ? 'Mark as read' : 'Mark as unread'}
                            primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
                        />
                    </MenuItem>
                </MenuList>
            </Popover>
        </Box>
    );
}

NotificationListRow.propTypes = {
    row: PropTypes.object,
    markAsRead: PropTypes.func,
    isLast: PropTypes.bool,
};

NotificationListRow.defaultProps = {
    isLast: false,
};
